/**
 * Dialogue 配置块 JSON Schema 定义
 */
export const dialogueSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/dialogue.json',
  title: 'Dialogue 配置',
  description: 'Dialogue（对话）配置数据结构',
  type: 'object',
  required: ['dialogue_id', 'npc_id'],
  properties: {
    dialogue_id: {
      type: 'string',
      title: 'Dialogue ID',
      description: 'Dialogue 唯一标识符',
      pattern: '^dialogue_[a-zA-Z0-9_]+$',
      minLength: 11,
      maxLength: 50,
      examples: ['dialogue_mysterious_man_intro', 'dialogue_merchant_trade'],
    },
    name: {
      type: 'string',
      title: '对话名称',
      description: '对话的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['神秘男人初次见面', '商人交易对话'],
    },
    npc_id: {
      type: 'string',
      title: 'NPC ID',
      description: '对话所属的 NPC',
      pattern: '^npc_[a-zA-Z0-9_]+$',
    },
    description: {
      type: 'string',
      title: '对话描述',
      description: '对话场景的描述',
      minLength: 0,
      maxLength: 500,
    },
    trigger_condition: {
      type: 'string',
      title: '触发条件',
      description: '对话触发的条件表达式',
    },
    dialogue_tree: {
      type: 'object',
      title: '对话树',
      description: '对话的树形结构',
      required: ['root_node'],
      properties: {
        root_node: {
          type: 'string',
          title: '根节点 ID',
          description: '对话的起始节点',
        },
        nodes: {
          type: 'object',
          title: '对话节点',
          description: '所有对话节点的映射',
          additionalProperties: {
            type: 'object',
            required: ['node_id', 'speaker', 'text'],
            properties: {
              node_id: {
                type: 'string',
                title: '节点 ID',
              },
              speaker: {
                type: 'string',
                title: '说话者',
                enum: ['NPC', '玩家', '旁白', '系统'],
              },
              text: {
                type: 'string',
                title: '对话文本',
                minLength: 1,
                maxLength: 2000,
              },
              emotion: {
                type: 'string',
                title: '情绪',
                description: '说话者的情绪',
                enum: ['开心', '愤怒', '悲伤', '平静', '惊讶', '恐惧', '其他'],
              },
              action: {
                type: 'string',
                title: '动作',
                description: '伴随对话的动作',
                maxLength: 100,
              },
              choices: {
                type: 'array',
                title: '选项',
                description: '玩家的回复选项',
                items: {
                  type: 'object',
                  required: ['text', 'next_node'],
                  properties: {
                    text: {
                      type: 'string',
                      title: '选项文本',
                      maxLength: 200,
                    },
                    next_node: {
                      type: 'string',
                      title: '下一节点',
                      description: '选择后跳转的节点 ID',
                    },
                    condition: {
                      type: 'string',
                      title: '显示条件',
                      description: '选项显示的条件表达式',
                    },
                    consequences: {
                      type: 'array',
                      title: '后果',
                      description: '选择此选项的后果',
                      items: {
                        type: 'object',
                        properties: {
                          type: {
                            type: 'string',
                            enum: ['变量修改', '物品获取', '好感度变化', '触发事件', '其他'],
                          },
                          target: {
                            type: 'string',
                          },
                          value: {
                            oneOf: [
                              { type: 'string' },
                              { type: 'number' },
                              { type: 'boolean' },
                            ],
                          },
                        },
                      },
                    },
                  },
                },
              },
              auto_continue: {
                type: 'boolean',
                title: '自动继续',
                description: '是否自动跳转到下一节点',
                default: false,
              },
              next_node: {
                type: 'string',
                title: '下一节点',
                description: '自动继续时跳转的节点 ID',
              },
              end_dialogue: {
                type: 'boolean',
                title: '结束对话',
                description: '是否在此节点结束对话',
                default: false,
              },
              voice_line: {
                type: 'string',
                title: '语音',
                description: '语音资源的路径',
                maxLength: 200,
              },
              animation: {
                type: 'string',
                title: '动画',
                description: '对话时播放的动画',
                maxLength: 200,
              },
            },
          },
        },
      },
    },
    variables: {
      type: 'object',
      title: '变量',
      description: '对话中使用的变量',
      additionalProperties: {
        type: ['string', 'number', 'boolean'],
      },
    },
    repeatable: {
      type: 'boolean',
      title: '可重复',
      description: '对话是否可以重复触发',
      default: true,
    },
    priority: {
      type: 'integer',
      title: '优先级',
      description: '多个对话可用时的优先级',
      minimum: 0,
      maximum: 100,
      default: 50,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type DialogueConfig = {
  dialogue_id: string
  name?: string
  npc_id: string
  description?: string
  trigger_condition?: string
  dialogue_tree: {
    root_node: string
    nodes: Record<
      string,
      {
        node_id: string
        speaker: string
        text: string
        emotion?: string
        action?: string
        choices?: Array<{
          text: string
          next_node: string
          condition?: string
          consequences?: Array<{
            type?: string
            target?: string
            value?: string | number | boolean
          }>
        }>
        auto_continue?: boolean
        next_node?: string
        end_dialogue?: boolean
        voice_line?: string
        animation?: string
      }
    >
  }
  variables?: Record<string, string | number | boolean>
  repeatable?: boolean
  priority?: number
  metadata?: Record<string, any>
}
